// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
/**
 * DossiersPage - /dossiers
 * Overzichtspagina voor alle dossiers.
 * Vervangt de directe links op het dashboard.
 */
import { useNavigate } from 'react-router-dom'
import { Scale, BookOpen, Flower2, ArrowRight, FolderOpen } from 'lucide-react'
import { BackButton }  from '@/components/ui/BackButton'
import { Card }        from '@/components/ui/Card'
import { DOSSIER_NOTARIS, DOSSIER_NABESTAANDEN, DOSSIER_UITVAART } from '@/lib/constants/routes'

const DOSSIERS = [
  {
    route:       DOSSIER_NABESTAANDEN,
    icon:        <BookOpen className="h-5 w-5" />,
    accent:      'bg-blue-50 text-blue-600',
    border:      'hover:border-blue-300 hover:bg-blue-50/50',
    title:       'Nabestaandendossier',
    description: 'Overzicht van alles wat uw naasten direct na overlijden nodig hebben: bankrekeningen, contacten, uitvaartwensen en documenten.',
    tag:         'Voor nabestaanden',
    tagColor:    'bg-blue-50 text-blue-700',
  },
  {
    route:       DOSSIER_NOTARIS,
    icon:        <Scale className="h-5 w-5" />,
    accent:      'bg-amber-50 text-amber-600',
    border:      'hover:border-amber-300 hover:bg-amber-50/50',
    title:       'Notarisdossier',
    description: 'Overzicht van uw notariële wensen: executeur, voogdij, erfgenamen en bezittingen. Ter voorbereiding op een notarisgesprek.',
    tag:         'Voor notaris',
    tagColor:    'bg-amber-50 text-amber-700',
  },
  {
    route:       DOSSIER_UITVAART,
    icon:        <Flower2 className="h-5 w-5" />,
    accent:      'bg-rose-50 text-rose-600',
    border:      'hover:border-rose-300 hover:bg-rose-50/50',
    title:       'Uitvaartverzorgerdossier',
    description: 'Deel praktische uitvaartinformatie met de uitvaartverzorger: wensen, verzekering en contactpersonen.',
    tag:         'Voor uitvaartverzorger',
    tagColor:    'bg-rose-50 text-rose-700',
  },
]

export function DossiersPage() {
  const navigate = useNavigate()

  return (
    <div className="flex flex-col gap-5">
      <div className="flex items-center gap-3">
        <BackButton />
      </div>

      <div>
        <div className="flex items-center gap-2 mb-1">
          <FolderOpen className="h-5 w-5 text-sage-600" />
          <h1 className="text-xl font-semibold text-ink">Mijn dossiers</h1>
        </div>
        <p className="text-sm text-ink-3">
          Uw vastgelegde gegevens samengevat in drie overzichten - voor uw naasten, uw notaris en de uitvaartverzorger.
        </p>
      </div>

      <div className="flex flex-col gap-3">
        {DOSSIERS.map(d => (
          <Card key={d.route} hoverable onClick={() => navigate(d.route)}>
            <div className="flex items-start gap-4">
              <div className={`flex h-11 w-11 items-center justify-center rounded-xl flex-shrink-0 ${d.accent}`}>
                {d.icon}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <p className="text-base font-semibold text-ink">{d.title}</p>
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${d.tagColor}`}>
                    {d.tag}
                  </span>
                </div>
                <p className="text-sm text-ink-3 leading-relaxed">{d.description}</p>
              </div>
              <ArrowRight className="h-4 w-4 text-ink-4 flex-shrink-0 mt-1" />
            </div>
          </Card>
        ))}
      </div>

      <div className="rounded-xl bg-sage-50 border border-sage-200 px-4 py-3">
        <p className="text-xs text-sage-700 leading-relaxed">
          De dossiers worden automatisch samengesteld op basis van wat u heeft ingevuld in WilsApp.
          Hoe meer u invult, hoe vollediger de dossiers zijn.
        </p>
      </div>
      <BackButton variant="bottom" />
    </div>
  )
}
